import React from 'react';

/**
 * Focus-ring state for elements that are styled inline.
 *
 * The dashboard tables carry their styles in `style` props, and inline styles
 * cannot express `:focus-visible` — so ask the element itself whether the
 * browser thinks this particular focus is worth drawing. That matters here
 * because rows are clickable: a mouse click focuses a row too, and a ring
 * around every clicked row is noise, while a keyboard user who cannot see
 * where they are is lost.
 *
 * Spread `focusProps` onto the element and use `isFocusVisible` to decide
 * whether to draw the outline.
 */
export function useFocusVisible() {
  const [isFocusVisible, setIsFocusVisible] = React.useState(false);

  const onFocus = React.useCallback((e: React.FocusEvent<HTMLElement>) => {
    try {
      setIsFocusVisible(e.currentTarget.matches(':focus-visible'));
    } catch {
      // Browser without :focus-visible — better a ring on click than none.
      setIsFocusVisible(true);
    }
  }, []);

  const onBlur = React.useCallback(() => setIsFocusVisible(false), []);

  return { isFocusVisible, focusProps: { onFocus, onBlur } };
}

export default useFocusVisible;
